/**
 * Todo identity + list merge for document sync redesign.
 * @see docs/superpowers/specs/2026-10-22-document-sync-redesign.md
 */

function newTodoId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function nowIso() {
  return new Date().toISOString();
}

function touchTodo(todo) {
  if (!todo || typeof todo !== "object") return todo;
  todo.updatedAt = nowIso();
  return todo;
}

function cloneList(list) {
  return Array.isArray(list) ? list.map((t) => (t && typeof t === "object" ? { ...t } : t)) : [];
}

/**
 * Ensure every todo has a stable id. Does not bump updatedAt on backfill
 * (avoids false conflict winners); caller marks list dirty if changed.
 * @param {unknown} list
 * @returns {{ list: object[], changed: boolean }}
 */
function ensureTodoIds(list) {
  const src = Array.isArray(list) ? list : [];
  let changed = false;
  const next = src.map((todo) => {
    if (!todo || typeof todo !== "object") return todo;
    if (todo.id) return todo;
    changed = true;
    return { ...todo, id: newTodoId() };
  });
  return { list: next, changed };
}

/**
 * At most one generated instance per repeatingEvent id.
 * @param {unknown} list
 * @returns {{ list: object[], changed: boolean }}
 */
function dedupeRepeatingInList(list) {
  const src = Array.isArray(list) ? list : [];
  if (!src.length) return { list: src, changed: false };
  const seen = new Set();
  const next = [];
  let changed = false;
  for (const todo of src) {
    const reId = todo && todo.repeatingEvent;
    if (reId) {
      if (seen.has(reId)) {
        changed = true;
        continue;
      }
      seen.add(reId);
    }
    next.push(todo);
  }
  return { list: next, changed };
}

function contentKey(todo) {
  if (!todo || typeof todo !== "object") return "";
  return JSON.stringify({
    text: todo.text,
    checked: !!todo.checked,
    desc: todo.desc || "",
    time: todo.time || null,
    alarm: !!todo.alarm,
    repeatingEvent: todo.repeatingEvent || null,
    subTaskList: todo.subTaskList || [],
  });
}

function parseUpdatedAt(todo) {
  if (!todo || !todo.updatedAt) return NaN;
  return Date.parse(todo.updatedAt);
}

function pickWinner(local, remote) {
  const lu = parseUpdatedAt(local);
  const ru = parseUpdatedAt(remote);
  if (!Number.isNaN(lu) && !Number.isNaN(ru)) {
    if (lu > ru) return local;
    if (ru > lu) return remote;
  }
  if (!Number.isNaN(lu) && Number.isNaN(ru)) return local;
  if (!Number.isNaN(ru) && Number.isNaN(lu)) return remote;
  // Tied / missing timestamps: prefer local when content differs (dirty intent)
  if (contentKey(local) !== contentKey(remote)) return local;
  return remote;
}

/**
 * Spec conflict UX: same id, substantive content differs, timestamps missing or equal.
 * Clear LWW (one side newer / only one side stamped) is not tied.
 */
function isTiedSubstantiveConflict(local, remote) {
  if (contentKey(local) === contentKey(remote)) return false;
  const lu = parseUpdatedAt(local);
  const ru = parseUpdatedAt(remote);
  if (!Number.isNaN(lu) && !Number.isNaN(ru) && lu !== ru) return false;
  if (!Number.isNaN(lu) && Number.isNaN(ru)) return false;
  if (!Number.isNaN(ru) && Number.isNaN(lu)) return false;
  return true;
}

/**
 * @param {object[]} local
 * @param {object[]} remote
 * @param {{ tombstones?: Record<string, unknown> }} [opts]
 * @returns {{ list: object[], changed: boolean, needsPush: boolean, tiedConflicts: string[] }}
 */
function mergeTodoList(local, remote, opts = {}) {
  const tombstones = opts.tombstones && typeof opts.tombstones === "object" ? opts.tombstones : {};
  const localEnsured = ensureTodoIds(cloneList(local));
  const remoteEnsured = ensureTodoIds(cloneList(remote));
  const localList = localEnsured.list;
  const remoteList = remoteEnsured.list;

  const localMap = new Map();
  const remoteMap = new Map();
  for (const t of localList) {
    if (t && t.id) localMap.set(t.id, t);
  }
  for (const t of remoteList) {
    if (t && t.id) remoteMap.set(t.id, t);
  }

  const allIds = new Set([...localMap.keys(), ...remoteMap.keys()]);
  const mergedById = new Map();
  const tiedConflicts = [];

  for (const id of allIds) {
    const l = localMap.get(id);
    const r = remoteMap.get(id);
    if (l && !r) {
      mergedById.set(id, l);
    } else if (!l && r) {
      // Spec: remote-only dropped when id is in local tombstones
      if (Object.prototype.hasOwnProperty.call(tombstones, id)) continue;
      mergedById.set(id, r);
    } else {
      if (isTiedSubstantiveConflict(l, r)) tiedConflicts.push(id);
      mergedById.set(id, pickWinner(l, r));
    }
  }

  // Local order wins; append remote-only ids not already placed
  const used = new Set();
  const ordered = [];
  for (const t of localList) {
    if (!t || !t.id || !mergedById.has(t.id) || used.has(t.id)) continue;
    ordered.push(mergedById.get(t.id));
    used.add(t.id);
  }
  for (const t of remoteList) {
    if (!t || !t.id || !mergedById.has(t.id) || used.has(t.id)) continue;
    ordered.push(mergedById.get(t.id));
    used.add(t.id);
  }

  const deduped = dedupeRepeatingInList(ordered);
  const list = deduped.list;

  const localKey = JSON.stringify(localList.map(contentKey));
  const resultKey = JSON.stringify(list.map(contentKey));
  const remoteKey = JSON.stringify(remoteList.map(contentKey));
  const changed =
    localEnsured.changed ||
    remoteEnsured.changed ||
    deduped.changed ||
    localKey !== resultKey;
  const needsPush = changed || resultKey !== remoteKey;

  return { list, changed, needsPush, tiedConflicts };
}

/**
 * Key-wise OR merge for repeating_event_by_date maps.
 * @param {Record<string, boolean>} local
 * @param {Record<string, boolean>} remote
 */
function mergeReByDate(local, remote) {
  const out = { ...(remote && typeof remote === "object" ? remote : {}) };
  const loc = local && typeof local === "object" ? local : {};
  let changed = false;
  for (const key of Object.keys(loc)) {
    if (loc[key] && !out[key]) {
      out[key] = true;
      changed = true;
    }
  }
  for (const key of Object.keys(out)) {
    if (!out[key]) {
      delete out[key];
      changed = true;
    }
  }
  const locKeys = Object.keys(loc).filter((k) => loc[k]).sort().join(",");
  const outKeys = Object.keys(out).filter((k) => out[k]).sort().join(",");
  if (locKeys !== outKeys) changed = true;
  return { map: out, changed, needsPush: locKeys !== outKeys };
}

module.exports = {
  newTodoId,
  nowIso,
  touchTodo,
  ensureTodoIds,
  dedupeRepeatingInList,
  mergeTodoList,
  mergeReByDate,
};
